import { useEffect, useState } from 'react';
import { getScheduledPaymentGroupFirstPayment } from '@/services/batchPayment';
import { TScheduledPaymentDetail } from '../types/ManagePayments';

// Session-scoped cache of resolved details, keyed by group id. Only successful lookups are
// cached (including an empty group) so reopening the same modal skips the network call; errors
// are intentionally left uncached so they can be retried.
const detailCache = new Map<string, TScheduledPaymentDetail | null>();

/**
 * Loads the first payment of a scheduled payment group to populate per-payment fields
 * (payee account, statement details) in the modal. Fails safe to `null` so the modal still
 * renders the group-level information if the lookup errors.
 */
export const useScheduledPaymentDetail = (groupId?: string, enabled = true) => {
  const [detail, setDetail] = useState<TScheduledPaymentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!groupId || !enabled) {
      setDetail(null);
      return;
    }

    if (detailCache.has(groupId)) {
      setDetail(detailCache.get(groupId) ?? null);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    getScheduledPaymentGroupFirstPayment(groupId)
      .then((result) => {
        detailCache.set(groupId, result);
        if (!isCancelled) setDetail(result);
      })
      .catch(() => {
        if (!isCancelled) setDetail(null);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [groupId, enabled]);

  return { detail, isLoading };
};
